import { Input } from 'components/ui/input';
import { VoiceSelection } from 'features/podcast/modals/VoiceSelection';
import { Maximize2, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { addCharacterDetails } from 'store/slices/podcastGen';
import { z } from 'zod';
import Select, { MultiValue } from 'react-select';
import { customStyles, options } from 'features/podcast/constants/SelectStyles';

export interface CharacterErrors {
  character_name?: string;
  age?: string;
  voice?: string;
  qualities?: string;
}

export const hostSchema = z.object({
  character_name: z
    .string()
    .min(1, {
      message: 'Name is required and must be at least 1 character long',
    })
    .max(50, { message: 'Name must not exceed 50 characters' })
    .refine((value) => !/\b(host|guest)\b/i.test(value), {
      message: "Name cannot contain 'host' or 'guest' as part of the name",
    }),
  age: z
    .number({
      invalid_type_error: 'Age is required',
      required_error: 'Age is required',
    })
    .int({ message: 'Age must be an integer' })
    .max(100, { message: 'Age must be less than 100' }  )
});

interface HostFormProps {
  index: number;
  isDuplicate: (name: string, index: number, role: 'host' | 'guest') => boolean;
  errors: CharacterErrors;
  setErrors: (index: number, errors: CharacterErrors) => void;
  validateField: (index: number, field: 'character_name' | 'age' | 'voice', value: any, role: 'host' | 'guest') => void;
}

const HostForm = ({ index, isDuplicate, errors, setErrors, validateField }: HostFormProps) => {
  const dispatch = useDispatch();
  const [voiceDialog, setVoiceDialog] = useState<boolean>(false);
  const [characterName, setCharacterName] = useState<string>('');
  const { hosts } = useSelector((state: RootState) => state.podcastGen);

  // to handle scroll on number input
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (document.activeElement === inputRef.current) {
        e.preventDefault();
      }
    };

    const currentInput = inputRef.current;
    if (currentInput) {
      currentInput.addEventListener('wheel', handleWheel);
    }

    return () => {
      if (currentInput) {
        currentInput.removeEventListener('wheel', handleWheel);
      }
    };
  }, []);

  const handleNameChange = () => {
    const name = characterName;
    if (name === '') {
      setErrors(index, { ...errors, character_name: 'Name is required' });
      return;
    }
    
    // Format the name
    const formattedName = name
      .replace(/\s+/g, '') // Remove spaces
      .replace(/^./, (char) => char.toUpperCase());

    // Character names should not be repeated
    if (isDuplicate(formattedName, index, 'host')) {
      setErrors(index, { ...errors, character_name: 'Name must be unique' });
      return;
    }

    // Clear previous error
    setErrors(index, { ...errors, character_name: undefined });
    
    // Validate the name using the schema
    try {
      hostSchema.parse({ ...hosts[index], character_name: formattedName });
      
      // Only dispatch if validation passes
      dispatch(
        addCharacterDetails({
          index,
          character_name: formattedName,
          role: 'host',
        })
      );
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldError = error.errors.find((err) => err.path[0] === 'character_name');
        setErrors(index, {
          ...errors,
          character_name: fieldError?.message,
        });
      }
    }
  };

  const handleAgeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const age = parseInt(e.target.value, 10);
    if (!isNaN(age)) {
      dispatch(addCharacterDetails({ index, age, role: 'host' }));
    }
  };

  // for select dropdown
  const handleQualitiesChange = (
    newValue: MultiValue<{ value: string; label: string }>
  ) => {
    // Extract values from the selected options
    const qualities = newValue.map((option) => option.value);
    dispatch(addCharacterDetails({ index, qualities, role: 'host' }));
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {/* voice selection */}
      <div className="py-0 flex flex-col gap-2 sm:gap-0">
        <span className="text-[14px] font-medium block sm:hidden">Voice</span>
        <div
          className="border border-input w-full text-[14px] cursor-pointer h-10 flex items-center px-3 py-2 ring-offset-background rounded-md bg-background justify-between"
          onClick={() => setVoiceDialog((prev) => !prev)}
        >
          <p
            className={`${hosts[index]?.voice_name ? 'text-text-primary' : 'text-text-secondary'} truncate mr-2 text-[16px] font-normal`}
          >
            {hosts[index]?.voice_name || 'Select'}
          </p>
          <Maximize2 className="flex-shrink-0" size={15} color="#60646C" />
        </div>
        {errors.voice ? (
          <p className="text-red-500 h-[10px] text-sm">{errors.voice}</p>
        ) : (
          <div className="h-[10px]" />
        )}
        {voiceDialog && (
          <>
            <div className="fixed inset-0 bg-black/50 z-[9998]" />
            <div className="fixed z-[9999] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[90vw] sm:w-auto">
              <button
                title="close"
                type="button"
                className="relative left-[98%] bottom-1 text-white z-10 hidden sm:block"
                onClick={() => setVoiceDialog(false)}
              >
                <X size={20} />
              </button>
              <VoiceSelection
                setVoiceDialog={setVoiceDialog}
                index={index}
                role="host"
                errors={errors}
                setErrors={(newErrors) => setErrors(index, newErrors)}
              />
            </div>
          </>
        )}
      </div>
      {/* name */}
      <div className="py-0 flex flex-col gap-2 sm:gap-0">
        <label
          htmlFor="host-name"
          className="text-[14px] font-medium block sm:hidden"
        >
          Name
        </label>
        <Input
          id="host-name"
          className={`w-full ${errors.character_name && 'border-red-500'} focus:ring-0 focus:outline-none placeholder:text-text-placeholder placeholder:text-[16px] placeholder:font-normal`}
          placeholder="John Doe"
          value={characterName || ''}
          onChange={(e) => setCharacterName(e.target.value)}
          onBlur={handleNameChange}
        />
        {errors.character_name ? (
          <p className="text-red-500 h-[10px] text-sm">
            {errors.character_name}
          </p>
        ) : (
          <div className="h-[10px]" />
        )}
      </div>
      {/* age */}
      <div className="py-0 flex flex-col gap-2 sm:gap-0">
        <label
          htmlFor="host-age"
          className="text-[14px] font-medium block sm:hidden"
        >
          Age
        </label>
        <Input
          id="host-age"
          ref={inputRef}
          className={`w-full ${errors.age && 'border-red-500'} focus:ring-0 focus:outline-none placeholder:text-[#B8BCBA] placeholder:text-[16px] placeholder:font-normal`}
          type="number"
          placeholder="Age"
          onChange={handleAgeChange}
          onBlur={(e) =>
            validateField(
              index,
              'age',
              e.target.value ? parseInt(e.target.value, 10) : undefined,
              'host'
            )
          }
        />
        {errors.age ? (
          <p className="text-red-500 text-sm h-[10px]">{errors.age}</p>
        ) : (
          <div className="h-[10px]" />
        )}
      </div>
      {/* characteristics */}
      <div className="py-0 flex flex-col gap-2 sm:gap-0">
        <label
          htmlFor="host-qualities"
          className="text-[14px] font-medium block sm:hidden"
        >
          Qualities
        </label>
        <Select
          id="host-qualities"
          closeMenuOnSelect={true}
          placeholder="Select"
          isSearchable={false}
          className="placeholder:text-text-placeholder placeholder:text-[16px] placeholder:font-normal"
          isMulti
          options={options}
          styles={{
            ...customStyles,
            container: (base) => ({
              ...base,
              width: '100%',
            }),
          }}
          onChange={handleQualitiesChange}
          noOptionsMessage={() => 'All selected'}
        />
      </div>
    </div>
  );
};

export default HostForm;
